import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

const CHANGELOG_STORAGE_PREFIX = 'mep_changelog_seen_';

/**
 * Simple hash function for changelog content fingerprinting.
 * Not cryptographic — just for change detection.
 */
function hashContent(content: string): string {
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

function getStorageKey(userId: string): string {
  return `${CHANGELOG_STORAGE_PREFIX}${userId}`;
}

export interface ChangelogSection {
  version: string;
  date: string;
  features: ChangelogFeature[];
}

export interface ChangelogFeature {
  title: string;
  content: string[];
}

/**
 * Parse the CHANGELOG.md content into structured sections.
 */
function parseChangelog(raw: string): ChangelogSection[] {
  const sections: ChangelogSection[] = [];
  const lines = raw.split('\n');

  let currentSection: ChangelogSection | null = null;
  let currentFeature: ChangelogFeature | null = null;

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip title and horizontal rules
    if (trimmed.startsWith('# ') || trimmed === '---' || trimmed === '') {
      if (trimmed === '' && currentFeature) {
        // Empty line may separate paragraphs — keep going
      }
      continue;
    }

    // Version header: ## v2.5.0 — Marco de 2026
    if (trimmed.startsWith('## ')) {
      // Save previous section
      if (currentSection) {
        if (currentFeature) {
          currentSection.features.push(currentFeature);
          currentFeature = null;
        }
        sections.push(currentSection);
      }

      const headerText = trimmed.replace('## ', '');
      const dashIndex = headerText.indexOf('—');
      const version = dashIndex >= 0 ? headerText.slice(0, dashIndex).trim() : headerText.trim();
      const date = dashIndex >= 0 ? headerText.slice(dashIndex + 1).trim() : '';

      currentSection = { version, date, features: [] };
      continue;
    }

    // Feature header: ### Dashboard mais completo
    if (trimmed.startsWith('### ')) {
      if (currentSection && currentFeature) {
        currentSection.features.push(currentFeature);
      }
      currentFeature = {
        title: trimmed.replace('### ', ''),
        content: [],
      };
      continue;
    }

    // Content line (paragraph or list item)
    if (currentFeature && trimmed) {
      currentFeature.content.push(trimmed);
    }
  }

  // Push last feature and section
  if (currentSection) {
    if (currentFeature) {
      currentSection.features.push(currentFeature);
    }
    sections.push(currentSection);
  }

  return sections;
}

export interface UseChangelogReturn {
  /** Whether the changelog modal should be shown */
  showChangelog: boolean;
  /** Parsed changelog sections */
  sections: ChangelogSection[];
  /** Raw changelog content */
  rawContent: string;
  /** Dismiss the changelog and mark as seen */
  dismissChangelog: () => void;
  /** Whether the changelog is still loading */
  isLoading: boolean;
}

export function useChangelog(): UseChangelogReturn {
  const { user } = useAuth();
  const [showChangelog, setShowChangelog] = useState(false);
  const [sections, setSections] = useState<ChangelogSection[]>([]);
  const [rawContent, setRawContent] = useState('');
  const [contentHash, setContentHash] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch and parse the changelog
  useEffect(() => {
    let cancelled = false;

    async function fetchChangelog() {
      try {
        const response = await fetch('/CHANGELOG.md');
        if (!response.ok) {
          setIsLoading(false);
          return;
        }
        const text = await response.text();
        if (cancelled) return;

        const hash = hashContent(text);
        const parsed = parseChangelog(text);

        setRawContent(text);
        setContentHash(hash);
        setSections(parsed);

        // Check if user has seen this version
        if (user?.id) {
          const seenHash = localStorage.getItem(getStorageKey(user.id));
          if (seenHash !== hash) {
            setShowChangelog(true);
          }
        }
      } catch {
        // Silently fail — changelog is non-critical
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    if (user?.id) {
      fetchChangelog();
    } else {
      setIsLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const dismissChangelog = useCallback(() => {
    setShowChangelog(false);
    if (user?.id && contentHash) {
      localStorage.setItem(getStorageKey(user.id), contentHash);
    }
  }, [user?.id, contentHash]);

  return {
    showChangelog,
    sections,
    rawContent,
    dismissChangelog,
    isLoading,
  };
}
